import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, ChevronLeft } from "lucide-react";
import Seo from "@/components/Seo";

const PATH = "/blog/top-incentive-travel-destinations-southeast-asia";
const TITLE = "Top 7 Incentive Travel Destinations in Southeast Asia";
const DESCRIPTION =
  "Discover the best Southeast Asia destinations for corporate incentive travel — from Singapore to Vietnam. Top picks from Trawelldmc's experts.";
const PUBLISHED_ISO = "2026-02-16";
const PUBLISHED_LABEL = "Feb 16, 2026";
const AUTHOR = "Trawelldmc Team";
const IMAGE =
  "https://images.unsplash.com/photo-1604999333679-b86d54738315?crop=entropy&cs=srgb&fm=webp&w=1600&q=80";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Blog", path: "/blog" },
  { name: TITLE, path: PATH },
];

const ARTICLE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: TITLE,
  description: DESCRIPTION,
  image: IMAGE,
  datePublished: PUBLISHED_ISO,
  dateModified: PUBLISHED_ISO,
  author: { "@type": "Organization", name: AUTHOR, url: "https://www.trawelldmc.com" },
  publisher: {
    "@type": "Organization",
    name: "Trawelldmc",
    logo: { "@type": "ImageObject", url: "https://www.trawelldmc.com/trawelldmc-logo-hd.png" },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": `https://www.trawelldmc.com${PATH}` },
};

const PICKS = [
  {
    n: "01",
    name: "Singapore",
    link: "/singapore-dmc",
    copy:
      "Singapore is the polished standard. Gardens by the Bay's Cloud Forest dome for private dinners, Marina Bay Sands' SkyPark infinity pool buyouts, and Sentosa's beachfront resorts give you a destination that feels both luxurious and effortless. English is the working language. Changi Airport connects 380+ cities. Visa-free for 160+ nationalities. For incentive groups who need polish, safety and global accessibility under one roof, Singapore is unmatched — and a perennial top-three pick on every Trawelldmc incentive shortlist.",
  },
  {
    n: "02",
    name: "Kuala Lumpur, Malaysia",
    link: "/malaysia-dmc",
    copy:
      "Kuala Lumpur is Southeast Asia's most under-priced premium incentive destination. The Petronas Twin Towers as a backdrop, Mandarin Oriental and Grand Hyatt ballrooms, and KLCC's surrounding restaurant scene give you 5-star inventory at 30–40% better value than Singapore. For corporate buyers under budget pressure who refuse to compromise on quality, KL consistently delivers. Combine with Langkawi for a beach finale and you've got a programme that punches well above its budget.",
  },
  {
    n: "03",
    name: "Bangkok, Thailand",
    link: "/thailand-dmc",
    copy:
      "Bangkok is the workhorse. The newly expanded Queen Sirikit National Convention Centre, IMPACT Muang Thong Thani, and a CBD packed with 5-star hotels — Mandarin Oriental, Park Hyatt, St. Regis, Waldorf Astoria — mean inventory is never a constraint. The food scene is among the best in the world. Rooftop bars deliver gala-dinner-worthy skylines. For incentive groups looking for energy, gastronomy and great value, Bangkok consistently outperforms expectations.",
  },
  {
    n: "04",
    name: "Phuket, Thailand",
    link: "/thailand-dmc",
    copy:
      "Phuket is Thailand's incentive jewel. Banyan Tree, Trisara, Anantara, Aman, Six Senses and the JW Marriott compete for high-end buyouts. Yacht charters and private island transfers deliver the signature moments incentive groups remember. Direct international flights make logistics easy. For beach-led incentive programmes where the brief is 'unforgettable, luxurious and easy to get to,' Phuket consistently shortlists in our top three.",
  },
  {
    n: "05",
    name: "Da Nang, Vietnam",
    link: "/vietnam-dmc",
    copy:
      "Da Nang is the rising star. The InterContinental Sun Peninsula Resort — a Bill Bensley masterpiece carved into a Vietnamese hillside — has won countless 'world's best resort' awards. Four Seasons Nam Hai nearby offers another tier of beach luxury. Direct flights from across Asia, an under-the-radar status, and dramatic coastline scenery make Da Nang the choice for incentive buyers who want a destination their delegates haven't already done three times.",
  },
  {
    n: "06",
    name: "Cebu, Philippines",
    link: "/philippines-dmc",
    copy:
      "Cebu delivers beach incentive heaven with international airlift. Shangri-La Mactan, Crimson Resort and the Plantation Bay resort offer luxury beach buyouts just 30 minutes from a major international airport. The English-speaking workforce eliminates language friction. Scuba diving, island-hopping by banca boat, and authentic Filipino hospitality round out the experience. For incentive groups wanting genuine beach drama at competitive rates, Cebu is the smart choice.",
  },
  {
    n: "07",
    name: "Bali, Indonesia (honourable mention)",
    link: null,
    copy:
      "Bali holds an honourable mention as Southeast Asia's most distinctive incentive market. Ubud's rice-paddy villa retreats (Four Seasons, Mandapa), Nusa Dua's beachfront resorts (St. Regis, Mulia), and Seminyak's design hotels offer the deepest luxury villa buyout market in Asia. The cultural overlay — temple ceremonies, Balinese dancers, traditional welcome rituals — adds storyline that competing destinations cannot match. Best for premium incentive groups of 50–200 pax where the brief is 'something different.'",
  },
];

export default function BlogTopIncentiveDestinations() {
  return (
    <div data-testid="blog-top-incentive">
      <Seo
        path={PATH}
        title="Top 7 Incentive Travel Destinations in Southeast Asia | Trawelldmc"
        description={DESCRIPTION}
        type="article"
        image={IMAGE}
        breadcrumb={BREADCRUMB}
        extraJsonLd={ARTICLE_JSONLD}
      />

      <section className="bg-[#0B192C] text-white">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white mb-8"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Blog
          </Link>
          <div className="overline !text-[#FF5238] mb-5">Incentive Travel</div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.08]">
            {TITLE}
          </h1>
          <div className="mt-8 flex items-center gap-5 text-sm text-white/60">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {PUBLISHED_LABEL}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              7 min read
            </span>
            <span>By {AUTHOR}</span>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-5xl mx-auto px-6 md:px-10 -mt-10 mb-12">
          <img
            src={IMAGE}
            alt="Southeast Asia luxury incentive destination — Trawelldmc DMC"
            width="1600"
            height="900"
            loading="lazy"
            decoding="async"
            className="w-full aspect-[16/9] object-cover rounded-md shadow-xl"
          />
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-6 md:px-10 pb-24">
        <p className="text-xl text-slate-700 leading-relaxed font-light">
          Southeast Asia is the world's most exciting incentive travel
          region. Across seven countries, you'll find luxury beach resorts,
          UNESCO heritage cities, dramatic island scenery, world-class
          gastronomy, and 5-star inventory at competitive rates. Over two
          decades of operating in the region, these are our top seven picks
          for incentive buyers.
        </p>

        <div className="mt-12 space-y-12">
          {PICKS.map((pick) => (
            <div key={pick.n} className="border-t-2 border-[#FF5238] pt-6">
              <div className="flex items-start gap-6">
                <div className="font-heading text-4xl md:text-5xl font-light text-[#FF5238] leading-none flex-shrink-0">
                  {pick.n}
                </div>
                <div className="flex-1">
                  <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-[#0B192C]">
                    {pick.name}
                  </h2>
                  <p className="text-slate-700 mt-3 leading-relaxed">
                    {pick.copy}
                  </p>
                  {pick.link && (
                    <Link
                      to={pick.link}
                      className="inline-flex items-center gap-2 text-[#FF5238] font-medium mt-4 hover:gap-3 transition-all"
                      data-testid={`pick-link-${pick.n}`}
                    >
                      Read our full {pick.name.split(",")[0]} guide
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 p-8 bg-[#FDFCF8] border-l-4 border-[#FF5238] rounded-r-md">
          <div className="overline mb-3">Plan with Trawelldmc</div>
          <h3 className="font-heading text-2xl font-medium text-[#0B192C] tracking-tight">
            Let's build your shortlist together.
          </h3>
          <p className="text-slate-600 mt-3 leading-relaxed">
            Tell us your headcount, budget per pax, and the storyline you
            want delegates to remember. We'll respond within 48 hours with
            a costed, themed proposal across the destinations that fit your
            brief.
          </p>
          <Link
            to="/inquiry?type=Incentive"
            className="btn-primary mt-6 inline-flex"
            data-testid="article2-cta"
          >
            Request an incentive proposal <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </article>
    </div>
  );
}
